import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Truck, Camera, X, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/vehicle-inspection")({
  component: VehicleInspectionPage,
  head: () => ({ meta: [{ title: "Vehicle Inspection — MissionReady" }] }),
});

type Vehicle = {
  id: string;
  label: string;
  license_plate: string | null;
  status: "active" | "inactive" | "in_inspection" | "retired";
};
type Inspection = {
  id: string;
  vehicle_id: string;
  inspection_type: string;
  mileage: number | null;
  fuel_level: string | null;
  damage_notes: string | null;
  result: string;
  photos: string[];
  checklist: Record<string, "pass" | "fail">;
  created_at: string;
  officer_id: string;
};

const CHECKLIST = [
  "Tires & wheels",
  "Lights & signals",
  "Brakes",
  "Mirrors & windows",
  "Fluids (oil, coolant, washer)",
  "Horn & wipers",
  "Seatbelts",
  "Emergency kit",
  "Body damage",
  "Interior cleanliness",
];

const FUEL_LABEL: Record<string, string> = {
  empty: "Empty", quarter: "¼", half: "½", three_quarter: "¾", full: "Full",
};

function VehicleInspectionPage() {
  const { currentOrg, user } = useAuth();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehicleId, setVehicleId] = useState<string>("");
  const [inspectionType, setInspectionType] = useState("pre_shift");
  const [mileage, setMileage] = useState<string>("");
  const [fuel, setFuel] = useState("half");
  const [checks, setChecks] = useState<Record<string, "pass" | "fail">>({});
  const [damage, setDamage] = useState("");
  const [photos, setPhotos] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<Inspection[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = async () => {
    if (!currentOrg) return;
    const [{ data: v, error: vErr }, { data: h, error: hErr }] = await Promise.all([
      supabase
        .from("vehicles")
        .select("id,label,license_plate,status")
        .eq("organization_id", currentOrg.id)
        .in("status", ["active", "in_inspection"])
        .order("label"),
      supabase
        .from("vehicle_inspections")
        .select("id,vehicle_id,inspection_type,mileage,fuel_level,damage_notes,result,photos,checklist,created_at,officer_id")
        .eq("organization_id", currentOrg.id)
        .order("created_at", { ascending: false })
        .limit(100),
    ]);
    if (vErr) toast.error(vErr.message);
    if (hErr) toast.error(hErr.message);
    setVehicles((v ?? []) as Vehicle[]);
    setHistory(
      ((h ?? []) as any[]).map((r) => ({
        ...r,
        photos: Array.isArray(r.photos) ? (r.photos as string[]) : [],
        checklist: (r.checklist ?? {}) as Record<string, "pass" | "fail">,
      })) as Inspection[],
    );
  };
  useEffect(() => { load(); }, [currentOrg?.id]);

  const uploadPhoto = async (file: File) => {
    if (!currentOrg || !user) return;
    const path = `${currentOrg.id}/${user.id}/${Date.now()}-${file.name.replace(/[^\w.\-]/g, "_")}`;
    const { error } = await supabase.storage.from("vehicle-inspections").upload(path, file);
    if (error) return toast.error(error.message);
    const { data, error: sErr } = await supabase.storage
      .from("vehicle-inspections")
      .createSignedUrl(path, 60 * 60 * 24 * 365);
    if (sErr) return toast.error(sErr.message);
    setPhotos((p) => [...p, data!.signedUrl]);
  };

  const submit = async () => {
    if (!currentOrg || !user) return;
    if (!vehicleId) return toast.error("Pick a vehicle");
    if (Object.keys(checks).length < CHECKLIST.length)
      return toast.error("Complete every checklist item");
    setBusy(true);
    const anyFail = Object.values(checks).some((v) => v === "fail");
    const { error } = await supabase.from("vehicle_inspections").insert({
      organization_id: currentOrg.id,
      vehicle_id: vehicleId,
      officer_id: user.id,
      inspection_type: inspectionType,
      mileage: mileage ? Number(mileage) : null,
      fuel_level: fuel,
      checklist: checks,
      damage_notes: damage || null,
      photos,
      result: anyFail ? "fail" : "pass",
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success(anyFail ? "Submitted (FAIL)" : "Submitted (PASS)");
    setVehicleId(""); setMileage(""); setChecks({}); setDamage(""); setPhotos([]);
    load();
  };

  const filteredHistory = useMemo(
    () => (vehicleId ? history.filter((h) => h.vehicle_id === vehicleId) : history),
    [vehicleId, history],
  );
  const vehLabel = (id: string) => vehicles.find((v) => v.id === id)?.label ?? "Vehicle";

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-2">
        <Truck className="h-6 w-6 text-[#CC0000]" />
        <div>
          <h1 className="text-2xl font-bold text-white">Vehicle Inspection</h1>
          <p className="text-sm text-white/60">Pre/post shift checks</p>
        </div>
      </div>

      {vehicles.length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-white/5 p-6 text-center">
          <p className="text-sm text-white/70">
            No vehicles in your organization yet.
          </p>
          <Link
            to="/vehicle-admin"
            className="mt-2 inline-block text-sm text-[#CC0000] hover:underline"
          >
            Add vehicles in Vehicle Admin →
          </Link>
        </div>
      ) : (
        <div className="rounded-lg border border-white/10 bg-white/5 p-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>Vehicle *</Label>
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger aria-label="Vehicle *"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.label}{v.license_plate ? ` · ${v.license_plate}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Inspection Type</Label>
              <Select value={inspectionType} onValueChange={setInspectionType}>
                <SelectTrigger aria-label="Inspection Type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pre_shift">Pre-shift</SelectItem>
                  <SelectItem value="post_shift">Post-shift</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mileage</Label>
              <Input aria-label="Mileage"
                type="number"
                inputMode="numeric"
                min={0}
                value={mileage}
                onChange={(e) => setMileage(e.target.value)}
              />
            </div>
            <div>
              <Label>Fuel Level</Label>
              <Select value={fuel} onValueChange={setFuel}>
                <SelectTrigger aria-label="Fuel Level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(FUEL_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="mb-2 block">
              Checklist ({Object.keys(checks).length}/{CHECKLIST.length})
            </Label>
            <div className="grid gap-1.5 md:grid-cols-2">
              {CHECKLIST.map((item) => (
                <div key={item} className="flex items-center justify-between rounded border border-white/10 bg-black/20 px-3 py-2">
                  <span className="text-sm text-white">{item}</span>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant={checks[item] === "pass" ? "default" : "ghost"}
                      onClick={() => setChecks({ ...checks, [item]: "pass" })}
                      className={checks[item] === "pass" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                    >Pass</Button>
                    <Button
                      size="sm"
                      variant={checks[item] === "fail" ? "default" : "ghost"}
                      onClick={() => setChecks({ ...checks, [item]: "fail" })}
                      className={checks[item] === "fail" ? "bg-[#CC0000] hover:bg-[#a30000]" : ""}
                    >Fail</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Damage notes</Label>
            <Textarea aria-label="Damage notes"
              value={damage}
              onChange={(e) => setDamage(e.target.value)}
              placeholder="Describe any damage…"
            />
          </div>

          <div>
            <Label className="mb-2 block">Photos ({photos.length})</Label>
            <div className="flex flex-wrap gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p} alt="" className="h-20 w-20 rounded object-cover border border-white/10" />
                  <button
                    onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                    className="absolute -top-1 -right-1 rounded-full bg-[#CC0000] p-0.5 text-white"
                  ><X className="h-3 w-3" /></button>
                </div>
              ))}
              <label className="flex h-20 w-20 cursor-pointer items-center justify-center rounded border border-dashed border-white/30 bg-black/30 hover:bg-black/50">
                <Camera className="h-5 w-5 text-white/60" />
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); }}
                />
              </label>
            </div>
          </div>

          <Button
            disabled={busy || !vehicleId}
            onClick={submit}
            className="w-full bg-[#CC0000] hover:bg-[#a30000] text-white"
          >
            {busy ? "Submitting…" : "Submit Inspection"}
          </Button>
        </div>
      )}

      <div>
        <h2 className="mb-2 text-lg font-semibold text-white">
          Inspection History {vehicleId && `· ${vehLabel(vehicleId)}`}
        </h2>
        <div className="space-y-2">
          {filteredHistory.length === 0 && (
            <p className="text-sm text-white/50">No inspections yet.</p>
          )}
          {filteredHistory.map((h) => {
            const isOpen = !!expanded[h.id];
            const failedItems = Object.entries(h.checklist).filter(([, v]) => v === "fail").map(([k]) => k);
            return (
              <div key={h.id} className="rounded-lg border border-white/10 bg-white/5">
                <button
                  type="button"
                  onClick={() => setExpanded({ ...expanded, [h.id]: !isOpen })}
                  className="flex w-full items-center justify-between gap-3 p-3 text-left"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-white">{vehLabel(h.vehicle_id)}</div>
                    <div className="text-xs text-white/50">
                      {new Date(h.created_at).toLocaleString()} · {h.inspection_type.replace("_", "-")}
                      {h.mileage != null ? ` · ${h.mileage.toLocaleString()} mi` : ""}
                      {h.fuel_level ? ` · fuel ${FUEL_LABEL[h.fuel_level] ?? h.fuel_level}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={h.result === "pass"
                      ? "bg-emerald-600/20 text-emerald-400 border-emerald-500/40 border"
                      : "bg-[#CC0000]/20 text-[#CC0000] border-[#CC0000]/40 border"}>
                      {h.result.toUpperCase()}
                    </Badge>
                    {isOpen ? <ChevronUp className="h-4 w-4 text-white/60" /> : <ChevronDown className="h-4 w-4 text-white/60" />}
                  </div>
                </button>
                {isOpen && (
                  <div className="space-y-3 border-t border-white/10 px-3 py-3">
                    {failedItems.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-[#CC0000] mb-1">Failed items</div>
                        <ul className="text-sm text-white space-y-0.5">
                          {failedItems.map((it) => <li key={it}>✗ {it}</li>)}
                        </ul>
                      </div>
                    )}
                    {Object.keys(h.checklist).length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-white/60 mb-1">Checklist</div>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5 text-sm">
                          {Object.entries(h.checklist).map(([k, v]) => (
                            <li key={k} className={v === "pass" ? "text-emerald-400" : "text-[#CC0000]"}>
                              {v === "pass" ? "✓" : "✗"} {k}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {h.damage_notes && (
                      <div>
                        <div className="text-xs font-semibold text-white/60 mb-1">Damage notes</div>
                        <p className="text-sm text-white whitespace-pre-wrap">{h.damage_notes}</p>
                      </div>
                    )}
                    {h.photos.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-white/60 mb-1">Photos ({h.photos.length})</div>
                        <div className="flex flex-wrap gap-2">
                          {h.photos.map((p, i) => (
                            <a key={i} href={p} target="_blank" rel="noreferrer">
                              <img src={p} alt="" className="h-20 w-20 rounded object-cover border border-white/10" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
